import { useEffect, useMemo, useState } from "react";
import { useGroupApi, imageUrl, MEAL_LABELS } from "../groupApi";
import LoadingComp from "../../../components/LoadingComp";
import { Sheet } from "./ui";
import "../../../styles/Groups.css";
import "../../../styles/CustomFood.css";

export const CUSTOM_FOOD_MAX = 80;

// Client-side mirror of the server rules (the server stays the source of truth).
export function customFoodError(raw) {
  const name = (raw || "").replace(/\s+/g, " ").trim();
  if (!name) return "اسم غذا رو بنویس";
  if (name.length < 2) return "اسم غذا خیلی کوتاهه";
  if (name.length > CUSTOM_FOOD_MAX) return `اسم غذا نباید بیشتر از ${CUSTOM_FOOD_MAX} حرف باشه`;
  if (/[<>]/.test(name)) return "اسم غذا نباید شامل < یا > باشه";
  return "";
}

// Reused by the group calendar and the personal calendar. Reuses the app's existing recipe
// endpoints: GET /search (text search) and GET /recipes (its tag-grouped suggestions).
// No second food database: "custom food" only hands a typed name back to the caller.
//
// Optional props
//   allowCustom / onPickCustom(name, meal, day)  show the "add a custom food" option
//   customHint                                   where the custom food will be added (shown in the explanation)
//   days [{iso,label}] / initialDay / occupiedByDay {iso: [meal,...]}   let the user also choose the day
export default function AddFoodModal({
  title, mealType, showMealPicker, onPick, onClose, busy,
  allowCustom, onPickCustom, customHint = "به تقویم",
  days, initialDay, occupiedByDay,
}) {
  const { call } = useGroupApi();
  const [q, setQ] = useState("");
  const [meal, setMeal] = useState(mealType);
  const [day, setDay] = useState(initialDay || (days && days[0] && days[0].iso) || null);
  const [suggestions, setSuggestions] = useState(null);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customErr, setCustomErr] = useState("");

  useEffect(() => {
    let ignore = false;
    call("recipes", { method: "GET" })
      .then((groups) => {
        if (ignore) return;
        const seen = new Set();
        const flat = [];
        (groups || []).forEach((g) =>
          (g.foods || []).slice(0, 4).forEach((f) => {
            if (!seen.has(f.recipe_id)) { seen.add(f.recipe_id); flat.push(f); }
          })
        );
        setSuggestions(flat.slice(0, 24));
      })
      .catch(() => !ignore && setSuggestions([]));
    return () => { ignore = true; };
  }, [call]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 3) { setResults(null); return; }
    let ignore = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const groups = await call(`search?q=${encodeURIComponent(term)}`, { method: "GET" });
        if (ignore) return;
        const seen = new Set();
        const flat = [];
        (groups || []).forEach((g) => (g.foods || []).forEach((f) => {
          if (!seen.has(f.recipe_id)) { seen.add(f.recipe_id); flat.push(f); }
        }));
        setResults(flat);
      } catch (e) {
        if (!ignore) setResults([]);
      } finally {
        if (!ignore) setSearching(false);
      }
    }, 350);
    return () => { ignore = true; clearTimeout(t); };
  }, [q, call]);

  const list = results !== null ? results : suggestions;
  const heading = useMemo(() => (results !== null ? "نتایج جستجو" : "پیشنهادها"), [results]);
  const mealPickerVisible = showMealPicker || !!days;
  const occupied = (occupiedByDay && day && occupiedByDay[day]) || [];
  const noResults = results !== null && results.length === 0 && !searching;

  const openCustom = () => {
    if (!customName && q.trim()) setCustomName(q.trim().slice(0, CUSTOM_FOOD_MAX));
    setCustomOpen(true);
  };

  const submitCustom = (e) => {
    e.preventDefault();
    const err = customFoodError(customName);
    setCustomErr(err);
    if (!err && !busy) onPickCustom(customName.replace(/\s+/g, " ").trim(), meal, day);
  };

  const pickers = (
    <>
      {days && (
        <label className="grpField">
          <span className="yekan">روز</span>
          <select className="grpInput" value={day || ""} onChange={(e) => setDay(e.target.value)} disabled={busy}>
            {days.map((d) => <option key={d.iso} value={d.iso}>{d.label}</option>)}
          </select>
        </label>
      )}
      {mealPickerVisible && (
        <>
          <div className="grpChoice" style={{ marginBottom: occupied.includes(meal) ? 6 : undefined }}>
            {Object.entries(MEAL_LABELS).map(([k, label]) => (
              <button key={k} type="button" className={`grpChip ${meal === k ? "grpChip-active" : ""}`} onClick={() => setMeal(k)}>
                {label}{occupied.includes(k) ? " ●" : ""}
              </button>
            ))}
          </div>
          {occupied.includes(meal) && (
            <p className="grey-2 caption-1 yekan" style={{ margin: "0 0 12px" }}>
              این وعده الان غذا داره؛ با افزودن غذای جدید جایگزین می‌شه.
            </p>
          )}
        </>
      )}
    </>
  );

  if (customOpen) {
    return (
      <Sheet title={title} onClose={onClose}>
        {pickers}
        <form onSubmit={submitCustom} noValidate>
          <div className="grpSectionTitle yekan">افزودن غذای دلخواه</div>
          <p className="grey-2 caption-1 yekan customFoodHelp">
            غذایی که دنبالشی رو پیدا نکردی؟ اسمش رو اینجا بنویس؛ همین الان {customHint} اضافه می‌شه و ما هم بررسی می‌کنیم
            که برای این غذا دستور پخت به فودپلنر اضافه کنیم. درخواستت به بزرگ‌تر شدن فودپلنر کمک می‌کنه.
          </p>
          <input
            className="grpInput"
            autoFocus
            maxLength={CUSTOM_FOOD_MAX}
            placeholder="مثلا: پاستای مخصوص مادربزرگ"
            value={customName}
            disabled={busy}
            aria-label="اسم غذای دلخواه"
            aria-invalid={!!customErr}
            onChange={(e) => { setCustomName(e.target.value); if (customErr) setCustomErr(""); }}
          />
          {customErr && <p className="customFoodError yekan" role="alert">{customErr}</p>}
          <div className="grpRowWrap" style={{ marginTop: 14 }}>
            <button type="submit" className="grpBtn" disabled={busy || !customName.trim()}>
              {busy ? "..." : "افزودن به تقویم"}
            </button>
            <button type="button" className="grpBtn grpBtn-ghost" disabled={busy} onClick={() => setCustomOpen(false)}>
              برگشت به غذاهای فودپلنر
            </button>
          </div>
        </form>
      </Sheet>
    );
  }

  return (
    <Sheet title={title} onClose={onClose}>
      {pickers}
      <input
        className="grpInput"
        autoFocus
        placeholder="جستجوی غذا (حداقل ۳ حرف)"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {allowCustom && (
        <button type="button" className={`customFoodLink yekan ${noResults ? "customFoodLink-strong" : ""}`} onClick={openCustom}>
          {noResults ? "این غذا رو پیدا نکردی؟ " : "غذات رو پیدا نکردی؟ "}
          <span>غذای دلخواه اضافه کن</span>
        </button>
      )}
      <div className="grpSectionTitle" style={{ marginTop: 14 }}>{heading}</div>
      {(list === null || searching) && <LoadingComp />}
      {list && !searching && list.length === 0 && (
        <p className="grey-2 caption-1 yekan">غذایی پیدا نشد</p>
      )}
      <div className="grpFoodGrid" style={{ opacity: busy ? 0.5 : 1, pointerEvents: busy ? "none" : "auto" }}>
        {(searching ? [] : list || []).map((f) => (
          <button key={f.recipe_id} type="button" className="grpFood" onClick={() => onPick(f, meal, day)}>
            {f.image_path ? <img src={imageUrl(f.image_path)} alt="" loading="lazy" /> : <img alt="" />}
            <p>{f.recipe_name}</p>
          </button>
        ))}
      </div>
    </Sheet>
  );
}
